/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useEffect } from 'react'

const q = (s) => document.querySelector(s)
const top = (el) => el.getBoundingClientRect().top + window.scrollY
const bottom = (el) => el.getBoundingClientRect().bottom + window.scrollY
const clamp01 = (v) => Math.max(0, Math.min(1, v))

function round(pts, r) {
  let d = 'M' + pts[0][0] + ' ' + pts[0][1]
  for (let i = 1; i < pts.length - 1; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    const c = pts[i + 1]
    const l1 = Math.hypot(b[0] - a[0], b[1] - a[1])
    const l2 = Math.hypot(c[0] - b[0], c[1] - b[1])
    const rr = Math.min(b.length > 2 ? b[2] : r, l1 / 2, l2 / 2)
    if (rr < 1) {
      d += ' L' + b[0] + ' ' + b[1]
      continue
    }
    const ax = b[0] + ((a[0] - b[0]) / l1) * rr
    const ay = b[1] + ((a[1] - b[1]) / l1) * rr
    const cx = b[0] + ((c[0] - b[0]) / l2) * rr
    const cy = b[1] + ((c[1] - b[1]) / l2) * rr
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0])
    d += ' L' + ax + ' ' + ay + ' A' + rr + ' ' + rr + ' 0 0 ' + (cross > 0 ? 1 : 0) + ' ' + cx + ' ' + cy
  }
  const l = pts[pts.length - 1]
  return d + ' L' + l[0] + ' ' + l[1]
}

function tidy(pts) {
  const out = []
  pts.forEach((p) => {
    const a = out[out.length - 1]
    if (a && a[0] === p[0] && a[1] === p[1]) return
    out.push(p)
  })
  return out.filter((b, i) => {
    const a = out[i - 1]
    const c = out[i + 1]
    if (!a || !c) return true
    return !((a[0] === b[0] && b[0] === c[0]) || (a[1] === b[1] && b[1] === c[1]))
  })
}

function pingpong(i, n) {
  if (n < 2) return 0
  const k = i % (2 * (n - 1))
  return k < n ? k : 2 * (n - 1) - k
}

function bez(p0, p1, p2, p3, n) {
  const out = []
  for (let i = 1; i <= n; i++) {
    const t = i / n
    const u = 1 - t
    out.push([
      u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
      u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
      0,
    ])
  }
  return out
}

function penLoop(tb, mid, yStop) {
  const c0 = (tb.left + tb.right) / 2
  const cx = Math.abs(c0 - mid) < 3 ? mid : c0
  const cy = (tb.top + tb.bottom) / 2 + window.scrollY
  const rx0 = (tb.right - tb.left) / 2 + 20
  const ry0 = (tb.bottom - tb.top) / 2 + 9
  const tilt = (-4 * Math.PI) / 180
  const ring = []
  const N = 110
  for (let i = 0; i <= N; i++) {
    const t = i / N
    const th = (-125 - 283 * (t + 0.012 * Math.sin(t * 18.8))) * (Math.PI / 180)
    const wob = 1 + 0.06 * Math.sin(2 * th + 1.1) + 0.035 * Math.sin(3 * th + 0.5)
    const rx = (rx0 + 6 * t) * wob
    const ry = (ry0 + 4 * t) * (1 + 0.04 * Math.sin(2 * th + 0.2))
    const ex = rx * Math.cos(th)
    const ey = ry * Math.sin(th)
    ring.push([cx + ex * Math.cos(tilt) - ey * Math.sin(tilt), cy + ex * Math.sin(tilt) + ey * Math.cos(tilt), 0])
  }
  const st = ring[0]
  const sx = ring[1][0] - st[0]
  const sy = ring[1][1] - st[1]
  const sl = Math.hypot(sx, sy) || 1
  const topY = cy - ry0 - 40
  const pts = [[mid, topY, 0]]
  pts.push(...bez([mid, topY], [mid, topY + 18], [st[0] - (sx / sl) * 26, st[1] - (sy / sl) * 26], st, 18))
  pts.push(...ring.slice(1))
  const pe = ring[ring.length - 1]
  const t1 = [mid - 40, cy + ry0 * 0.15]
  pts.push(...bez(pe, [pe[0] - 42, pe[1] - 7], [mid - 52, pe[1] - 3], t1, 22))
  const y2 = Math.max(t1[1] + 30, yStop - 64)
  pts.push(...bez(t1, [mid - 33, t1[1] + 30], [mid, y2], [mid, yStop - 6], 26))
  pts.push([mid, yStop - 4, 0])
  const e1 = t1
  const ey0 = e1[1]
  return { pts, y: ey0 }
}

function mazePoints(cards, W, xl, mid, gB, btn, tb) {
  const rects = cards.map((c) => {
    const b = c.getBoundingClientRect()
    return { l: b.left, r: b.right, t: b.top + window.scrollY, b: b.bottom + window.scrollY }
  })
  rects.sort((a, b) => a.t - b.t || a.l - b.l)
  const rows = []
  rects.forEach((c) => {
    const last = rows[rows.length - 1]
    if (last && Math.abs(last.t - c.t) < 4) {
      last.cards.push(c)
      last.b = Math.max(last.b, c.b)
    } else rows.push({ t: c.t, b: c.b, cards: [c] })
  })
  const first = rows[0].cards.slice().sort((a, b) => a.l - b.l)
  const cl = Math.min(...rects.map((c) => c.l))
  const mL = Math.max(5, Math.min(xl, cl - 6))
  const mR = W - mL
  const gaps = first.slice(1).map((c, i) => (first[i].r + c.l) / 2)
  const C = [mL, ...gaps, mR]
  let ri = C.findIndex((x) => x > mid + 5)
  if (ri < 0) ri = C.length - 1
  const chan = (i) => {
    if (i === 0) return mid
    const n = C.length
    const per = 2 * (n - 1)
    const k = (n - 1 - ri + (i - 1)) % per
    const rev = k <= n - 1 ? k : per - k
    return C[n - 1 - rev]
  }
  const gap = rows.length > 1 ? rows[1].t - rows[0].b : 16
  const rad = Math.max(3, Math.min(8, gap / 2 - 1))
  const loop = penLoop(tb, mid, rows[0].t)
  const pts = [...loop.pts]
  rows.forEach((row, i) => {
    if (i < rows.length - 1) {
      const y = (row.b + rows[i + 1].t) / 2
      pts.push([chan(i), y, rad], [chan(i + 1), y, rad])
    }
  })
  let x = chan(rows.length - 1)
  const lastB = rows[rows.length - 1].b
  if (btn && x > btn.left - 14 && x < btn.right + 14 && x !== mid) {
    const yx = lastB + 13
    const mx = x < mid ? mL : mR
    pts.push([x, yx, rad], [mx, yx, rad])
    x = mx
  }
  const yBtn = btn ? (btn.top + btn.bottom) / 2 + window.scrollY : gB - 20
  const edge = btn ? (x < mid ? btn.left : btn.right) : mid
  let arrive = null
  if (x !== mid) {
    pts.push([x, yBtn, rad])
    arrive = [...pts, [edge, yBtn, 0]]
    pts.push([mid, yBtn, rad])
  } else arrive = [...pts, [mid, btn ? btn.top + window.scrollY : yBtn, 0]]
  pts.push([mid, gB])
  return { pts, arrive, clip: null }
}

function samples(p) {
  const t = p.getTotalLength()
  const s = [[0, 0]]
  let c = 0
  let pr = p.getPointAtLength(0)
  for (let l = 8; l < t; l += 8) {
    const pt = p.getPointAtLength(l)
    c += Math.abs(pt.y - pr.y) + 0.6 * Math.abs(pt.x - pr.x)
    s.push([l, c])
    pr = pt
  }
  const e = p.getPointAtLength(t)
  c += Math.abs(e.y - pr.y) + 0.6 * Math.abs(e.x - pr.x)
  s.push([t, c])
  return s
}

function anchors(p) {
  const t = p.getTotalLength()
  const a = [[p.getPointAtLength(0).y, 0]]
  for (let l = 12; l < t; l += 12) {
    const y = p.getPointAtLength(l).y
    if (y >= a[a.length - 1][0] + 20) a.push([y, l])
  }
  const e = p.getPointAtLength(t).y
  a.push([Math.max(e, a[a.length - 1][0] + 1), t])
  return a
}

function lenAtY(a, y) {
  if (y <= a[0][0]) return 0
  const n = a.length - 1
  if (y >= a[n][0]) return a[n][1]
  let i = 0
  while (i < n - 1 && a[i + 1][0] <= y) i++
  const f = (y - a[i][0]) / Math.max(1e-6, a[i + 1][0] - a[i][0])
  return a[i][1] + (a[i + 1][1] - a[i][1]) * f
}

function atCost(s, f) {
  const C = s[s.length - 1][1] * clamp01(f)
  let b = s[0][0]
  for (let i = 0; i < s.length; i++) {
    if (s[i][1] <= C) b = s[i][0]
    else break
  }
  return b
}

function roundRect(x0, x1, y0, y1, r) {
  const cx = (x0 + x1) / 2
  return [
    'M' + cx + ' ' + y0 + ' H' + (x1 - r) + ' A' + r + ' ' + r + ' 0 0 1 ' + x1 + ' ' + (y0 + r) + ' V' + (y1 - r) + ' A' + r + ' ' + r + ' 0 0 1 ' + (x1 - r) + ' ' + y1 + ' H' + cx,
    'M' + cx + ' ' + y0 + ' H' + (x0 + r) + ' A' + r + ' ' + r + ' 0 0 0 ' + x0 + ' ' + (y0 + r) + ' V' + (y1 - r) + ' A' + r + ' ' + r + ' 0 0 0 ' + (x0 + r) + ' ' + y1 + ' H' + cx,
  ]
}

export default function useScrollPath() {
  useEffect(() => {
    const svg = document.getElementById('seq-path')
    const P = [document.getElementById('seq-p1')]
    const topSvg = document.getElementById('seq-path-top')
    const F = [document.getElementById('seq-f1'), document.getElementById('seq-f2')]
    if (!svg || !P[0] || !topSvg) return undefined
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const ease = window.matchMedia('(pointer: coarse)').matches ? 1 : 0.14
    let lastY = -1
    let idle = 0
    const cur = [0, 0, 0, 0, 0]
    const mcur = [0, 0, 0, 0, 0]
    let tg = [0, 0, 0, 0, 0]
    let G = null
    let raf = 0
    let timer = 0
    let sig = ''
    const signature = () => document.documentElement.clientWidth + 'x' + document.body.offsetHeight
    const pageHeight = () => Math.max(document.body.offsetHeight, window.innerHeight)

    function buildMobileFoot() {
      topSvg.querySelectorAll('.mfp').forEach((n) => n.remove())
      const lg = q('.lp-foot-logo-m')
      const els = [q('.lp-foot-col-l'), q('.lp-foot-col-c'), q('.lp-foot-col-r'), q('.lp-foot-copy')]
      if (!lg || els.some((e) => !e)) return
      const lr = lg.getBoundingClientRect()
      const secs = els.map((el) => {
        const r = el.getBoundingClientRect()
        const x0 = r.left + 0.5
        const x1 = r.right - 0.5
        const y0 = r.top + window.scrollY + 0.5
        const y1 = r.bottom + window.scrollY - 0.5
        const rad = Math.min(parseFloat(getComputedStyle(el).borderTopLeftRadius) || 10, (x1 - x0) / 2, (y1 - y0) / 2)
        const ps = roundRect(x0, x1, y0, y1, rad).map((s) => {
          const p = document.createElementNS('http://www.w3.org/2000/svg', 'path')
          p.setAttribute('class', 'mfp')
          p.setAttribute('d', s)
          topSvg.appendChild(p)
          const l = p.getTotalLength()
          p.style.strokeDasharray = l
          p.style.strokeDashoffset = l
          return { p, l }
        })
        return { top: r.top + window.scrollY, h: r.height, ps }
      })
      lg.style.setProperty('--mgd', 2 * (Math.hypot(lr.width / 2, lr.height) + 40) + 'px')
      G.mf = { lg, top: lr.top + window.scrollY, h: lr.height, rmax: Math.hypot(lr.width / 2, lr.height) + 40, secs, last: null }
    }

    function buildFoot(fl, fm, fR, ft, W, H, m) {
      topSvg.setAttribute('viewBox', '0 0 ' + W + ' ' + H)
      topSvg.style.height = H + 'px'
      G.mf = null
      if (m) {
        F[0].setAttribute('d', '')
        F[1].setAttribute('d', '')
        G.f = [0, 0]
        buildMobileFoot()
        return
      }
      const a = fl.getBoundingClientRect()
      const b = fm.getBoundingClientRect()
      const c = fR.getBoundingClientRect()
      const y = a.top + window.scrollY + 0.5
      const yb = ft.getBoundingClientRect().bottom + window.scrollY
      const rr = parseFloat(getComputedStyle(fR).borderTopRightRadius) || 10
      const rl = parseFloat(getComputedStyle(fl).borderTopLeftRadius) || 10
      F[0].setAttribute('d', 'M' + b.right + ' ' + y + ' H' + (c.right - rr - 0.5) + ' A' + rr + ' ' + rr + ' 0 0 1 ' + (c.right - 0.5) + ' ' + (y + rr) + ' V' + yb)
      F[1].setAttribute('d', 'M' + b.left + ' ' + y + ' H' + (a.left + rl + 0.5) + ' A' + rl + ' ' + rl + ' 0 0 0 ' + (a.left + 0.5) + ' ' + (y + rl) + ' V' + yb)
      G.f = F.map((p) => {
        const t = p.getTotalLength()
        p.style.strokeDasharray = t
        return t
      })
    }

    function build() {
      const intro = q('#hp-top-sec')
      const tm = q('#team .tm-card')
      const car = q('.lp-programs')
      const fr = q('.ba-frame')
      const pr = q('#pricing')
      const ex = q('#seq-videos')
      const bt = q('#seq-videos .lp-videos-all-a')
      const ft = q('#seq-footer')
      const fl = q('.lp-foot-col-r')
      const fm = q('.lp-foot-col-c')
      const fR = q('.lp-foot-col-l')
      const gl = q('.lp-foot-logo')
      if (!intro || !tm || !car || !fr || !pr || !ex || !bt || !ft || !fl || !fm || !fR || !gl) return
      const W = document.documentElement.clientWidth
      const H = pageHeight()
      const m = W < 768
      const xl = m ? 10 : 34
      const xr = W - xl
      const mid = W / 2
      const r = m ? 22 : 44
      const f = fr.getBoundingClientRect()
      const fx0 = f.left
      const fx1 = f.right
      const fy0 = f.top + window.scrollY
      const fy1 = f.bottom + window.scrollY
      const g1 = (bottom(intro) + top(tm.closest('#team'))) / 2
      const yT = (top(tm) + bottom(tm)) / 2
      const g4 = (bottom(pr) + top(ex)) / 2
      const cards = document.querySelectorAll('.seq-vl-card')
      let xv = xr
      if (m && cards[0]) {
        const c0 = cards[0].getBoundingClientRect()
        const c1 = cards[1] ? cards[1].getBoundingClientRect() : null
        if (c1 && Math.abs(c1.top - c0.top) < 4) xv = (Math.min(c0.right, c1.right) + Math.max(c0.left, c1.left)) / 2
        else {
          const th = cards[0].querySelector('.seq-vl-thumb').getBoundingClientRect()
          xv = (th.left + th.right) / 2
        }
      }
      const yB = (top(bt) + bottom(bt)) / 2
      const yE = ((m ? q('.lp-foot-logo-m') : gl) || fm).getBoundingClientRect().top + window.scrollY - 2
      svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H)
      svg.style.height = H + 'px'
      const o = m ? [12, 9, 7, 5] : [16, 12, 9, 6]
      const s0 = fy0 - (m ? 70 : 90)
      const r2 = m ? 20 : 28
      const d0 = round([[mid, bottom(intro)], [mid, g1], [xr, g1], [xr, yT], [mid, yT], [mid, s0]], r)
      const d1 = round([[mid, s0], [mid, fy0 - o[0]], [fx1 + o[0], fy0 - o[0]], [fx1 + o[1], fy1 + o[1]], [fx0 - o[2], fy1 + o[2]], [fx0 - o[3], fy0 - o[3]], [mid, fy0 - o[3]], [mid, fy0 + 60]], r2)
      const arts = Array.from(document.querySelectorAll('.lp-art-card'))
      const sec = q('#seq-articles')
      const tx = m ? xv : xl
      let mzr = null
      let d2pts = [[mid, fy0 + 60], [mid, g4], [tx, g4], [tx, yB], [mid, yB], [mid, yE]]
      if (sec && arts.length > 1) {
        const gB = (bottom(sec) + top(ex)) / 2
        const btnEl = q('.lp-art-all a')
        const tEl = q('#seq-articles .lp-art-title a')
        const sEl = q('#seq-articles .lp-art-sub')
        const textRect = (el) => {
          const rg = document.createRange()
          rg.selectNodeContents(el)
          return rg.getBoundingClientRect()
        }
        const mz = tEl ? mazePoints(arts, W, xl, mid, gB, btnEl ? btnEl.getBoundingClientRect() : null, textRect(tEl)) : null
        if (mz) {
          d2pts = [[mid, fy0 + 60], ...mz.pts, [mid, yB], [mid, yE]]
          mzr = { mz, btnEl }
        }
      }
      const d2 = round(tidy(d2pts), r)
      const tmp = [d0, d1, d2].map((d, k) => {
        const t = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        t.setAttribute('d', d)
        svg.appendChild(t)
        const out = { len: t.getTotalLength(), smp: k === 0 ? samples(t) : null, anc: k === 2 ? anchors(t) : null }
        svg.removeChild(t)
        return out
      })
      const tail = (d) => d.replace(/^M\S+ \S+/, '')
      P[0].setAttribute('d', d0 + tail(d1) + tail(d2))
      let btnAt = Infinity
      if (mzr && mzr.mz.arrive) {
        const ap = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        ap.setAttribute('d', round(tidy([[mid, fy0 + 60], ...mzr.mz.arrive]), r))
        svg.appendChild(ap)
        btnAt = tmp[0].len + tmp[1].len + ap.getTotalLength()
        svg.removeChild(ap)
      }
      const T1 = tmp[1].len
      const L2 = T1 - (o[3] + 60 - Math.min(r2, (o[3] + 60) / 2))
      G = {
        s0,
        L2,
        tc: (fy0 + fy1) / 2 + window.innerHeight * 0.2,
        te: (fy0 + fy1) / 2 + window.innerHeight * 0.3,
        T: P[0].getTotalLength(),
        T1,
        s1: tmp[0].smp,
        a3: tmp[2].anc,
        y0a: bottom(intro),
        y1a: s0,
        y0c: Math.max(fy0 + 60, (fy0 + fy1) / 2 + window.innerHeight * 0.3),
        y1c: yE,
        fy0,
        fh: fy1 - fy0,
        yE,
        H,
        gl,
        gw: gl.getBoundingClientRect().width,
        f: null,
        mf: null,
      }
      P[0].style.strokeDasharray = G.T
      G.btn = mzr ? mzr.btnEl : null
      G.btnAt = btnAt
      G.btnOn = null
      buildFoot(fl, fm, fR, ft, W, H, m)
      P[0].style.strokeDasharray = G.T
      sig = signature()
      update(true)
    }

    function targets() {
      const t = window.scrollY + window.innerHeight * 0.7
      const out = [0, 0, 0, 0, 0]
      const a = atCost(G.s1, (t - G.y0a) / Math.max(1, G.y1a - G.y0a))
      const b = t <= G.tc ? G.L2 * clamp01((t - G.s0) / Math.max(1, G.tc - G.s0)) : G.L2 + (G.T1 - G.L2) * clamp01((t - G.tc) / Math.max(1, G.te - G.tc))
      const c = t <= G.y0c ? 0 : lenAtY(G.a3, t)
      out[0] = a + b + c
      const R = Math.max(2, G.H - window.innerHeight * 0.3 - G.yE)
      out[3] = clamp01((t - G.yE) / (R * 0.5))
      out[4] = clamp01((t - G.yE - R * 0.5) / (R * 0.5))
      return out
    }

    function mobileTargets() {
      const sm = clamp01(window.scrollY / Math.max(1, pageHeight() - window.innerHeight))
      const t = window.scrollY + window.innerHeight * (0.7 + 0.3 * sm * sm * sm)
      const M = G.mf
      const o = [clamp01((t - M.top) / Math.max(1, M.h * 0.9))]
      let prevEnd = M.top + M.h * 0.9
      M.secs.forEach((s) => {
        const st = Math.max(s.top, prevEnd)
        o.push(clamp01((t - st) / Math.max(1, s.h * 0.85)))
        prevEnd = st + s.h * 0.85
      })
      return o
    }

    function applyMobile() {
      const M = G.mf
      const L = M.last || (M.last = [-1, -1, -1, -1, -1])
      if (L[0] !== mcur[0]) {
        L[0] = mcur[0]
        M.lg.style.setProperty('--mrs', mcur[0])
        M.lg.style.setProperty('--mga', Math.min(1, mcur[0] * 4))
      }
      M.secs.forEach((s, i) => {
        const k = i + 1
        if (L[k] === mcur[k]) return
        L[k] = mcur[k]
        s.ps.forEach((o) => (o.p.style.strokeDashoffset = o.l * (1 - mcur[k])))
      })
    }

    function update(now) {
      if (!G) return
      tg = targets()
      if (reduce || now === true) {
        for (let i = 0; i < 5; i++) cur[i] = reduce ? (i > 2 ? 1 : i === 0 ? G.T : 0) : tg[i]
        if (G.mf) {
          const m0 = mobileTargets()
          for (let k = 0; k < 5; k++) mcur[k] = reduce ? 1 : m0[k]
        }
      }
      if (!raf) raf = requestAnimationFrame(step)
    }

    function step() {
      raf = 0
      let more = false
      tg = targets()
      const y = window.scrollY
      if (y !== lastY) {
        lastY = y
        idle = 0
        more = true
      } else if (idle++ < 3) more = true
      for (let i = 0; i < 5; i++) {
        cur[i] += (tg[i] - cur[i]) * ease
        if (Math.abs(tg[i] - cur[i]) < (i > 2 ? 0.002 : 0.5)) cur[i] = tg[i]
        else more = true
      }
      const drawn = Math.min(cur[0], G.T)
      if (drawn >= G.T - 0.5) P[0].style.strokeDasharray = 'none'
      else {
        P[0].style.strokeDasharray = G.T
        P[0].style.strokeDashoffset = G.T - drawn
      }
      if (G.btn) {
        const on = cur[0] >= G.btnAt
        if (on !== G.btnOn) {
          G.btnOn = on
          G.btn.classList.toggle('lp-art-all-on', on)
        }
      }
      G.gl.style.setProperty('--rr', cur[3] * (G.gw / 2 + 50) + 'px')
      G.gl.style.setProperty('--ga', Math.min(1, cur[3] * 4))
      if (G.f) for (let k = 0; k < 2; k++) F[k].style.strokeDashoffset = G.f[k] * (1 - cur[4])
      if (G.mf) {
        const mt = mobileTargets()
        for (let z = 0; z < 5; z++) {
          mcur[z] += (mt[z] - mcur[z]) * ease
          if (Math.abs(mt[z] - mcur[z]) < 0.002) mcur[z] = mt[z]
          else more = true
        }
        applyMobile()
      }
      if (more) raf = requestAnimationFrame(step)
    }

    const onScroll = () => update()
    const rebuild = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        if (sig && signature() === sig) return
        build()
      }, 200)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', rebuild)
    window.addEventListener('load', build)
    const ro = 'ResizeObserver' in window ? new ResizeObserver(rebuild) : null
    if (ro) {
      ro.observe(document.body)
      ;['#hp-top-sec', '#seq-articles', '#team .tm-card', '.lp-programs', '.ba-frame', '#pricing', '#seq-videos', '#seq-footer', '.lp-foot-cols'].forEach((sel) => {
        const el = q(sel)
        if (el) ro.observe(el)
      })
    }
    const onAsset = (e) => {
      if (e.target && (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO')) rebuild()
    }
    document.addEventListener('load', onAsset, true)
    window.addEventListener('orientationchange', rebuild)
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(rebuild)
    build()
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', rebuild)
      window.removeEventListener('load', build)
      document.removeEventListener('load', onAsset, true)
      window.removeEventListener('orientationchange', rebuild)
      if (ro) ro.disconnect()
      clearTimeout(timer)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])
}
